import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AdminPageLayout, Section, Badge } from '../../components';
import {
  Listing,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  ViewAction,
  CreateAction,
  CopyAction
} from '../../components/listing/v2/ListingAction';
import {
  Timer,
  Calendar,
  ListOrdered,
  Lock,
  AlertTriangle
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useTemplateState, TemplateStateProvider } from '../../contexts/TemplateStateContext';
import apiService from '../../services/api';

const SessionTemplateManagementContent = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  // Use State Pattern for context-specific behavior
  const { state, clubId, branchId } = useTemplateState();

  const [systemTemplates, setSystemTemplates] = useState([]);
  const [clubSessions, setClubSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get paths from state
  const paths = state.getSessionNavigationPaths(branchId);

  const loadTemplatesAndSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Debug logging
      console.log('[SessionTemplateManagement] Loading templates with context:', {
        clubId,
        branchId,
        contextType: state.getScope()
      });

      // Validate branchId for branch/coach contexts
      if (state.getScope() === 'branch' && !branchId) {
        const errorMsg = 'Branch ID is required but not available. Please ensure you are logged in as a coach with a branch assignment.';
        console.error('[SessionTemplateManagement]', errorMsg);
        setError(errorMsg);
        showError(errorMsg);
        setLoading(false);
        return;
      }

      const activeOnly = filters.status === 'active';
      const endpoint = state.getSessionTemplateEndpoint(clubId, branchId);

      const data = await apiService.get(`${endpoint}?active_only=${activeOnly}&include_activities=true`);
      const allTemplates = data?.templates || [];

      // Use state pattern to separate templates
      const { systemTemplates: sysTemplates, customTemplates: customSessions } =
        state.separateTemplates(allTemplates);

      setSystemTemplates(sysTemplates);
      setClubSessions(customSessions);
    } catch (err) {
      setError(err.message);
      showError(`Error loading session templates: ${err.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, state, clubId, branchId]);

  useEffect(() => {
    loadTemplatesAndSessions();
  }, [loadTemplatesAndSessions]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCopyTemplate = async (template) => {
    try {
      const targetScope = state.getScope();
      const targetScopeId = branchId || clubId;
      const copyLabel = state.getCopyLabel();

      await apiService.post(`/api/clubs/${clubId}/session-templates/${template.id}/copy`, {
        target_scope: targetScope,
        target_scope_id: targetScopeId,
        customizations: {
          name: template.name
        }
      });

      showSuccess(`Session template "${template.name}" copied to ${copyLabel.replace('Copy to ', '').toLowerCase()}.`);
      // Small delay to ensure Firestore has propagated the write
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadTemplatesAndSessions();
    } catch (err) {
      showError(`Error copying session template: ${err.message}`);
    }
  };

  const handleDeleteSession = async (session) => {
    setIsDeleting(true);

    try {
      const endpoint = state.getSessionTemplateEndpoint(clubId, branchId);
      await apiService.delete(`${endpoint}/${session.id}`);

      const scopeName = state.getScope();
      showSuccess(`${scopeName.charAt(0).toUpperCase() + scopeName.slice(1)} session template "${session.name}" deleted successfully.`);

      setShowDeleteModal(null);
      await loadTemplatesAndSessions();
    } catch (err) {
      showError(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const applyFilters = (items) => {
    return items.filter(item => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        if (!item.name.toLowerCase().includes(searchTerm) &&
          !(item.description && item.description.toLowerCase().includes(searchTerm))) {
          return false;
        }
      }
      return true;
    });
  };

  const filteredSystemTemplates = applyFilters(systemTemplates);
  const filteredClubSessions = applyFilters(clubSessions);

  const isAlreadyCopied = (templateId) => {
    // Check if any club-level template (not inherited) was created from this platform template
    return clubSessions.some(session => 
      session.source_scope === 'club' && session.source_template_id === templateId
    );
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ status: 'all', search: '' })}
    />
  );

  const renderSessionItem = (item, isSystemTemplate = false) => {
    const alreadyCopied = isSystemTemplate && isAlreadyCopied(item.id);
    const activityCount = item.activities?.length || 0;
    const totalActivitiesDuration = item.activities?.reduce((sum, activity) => sum + (activity.duration_mins || 0), 0) || 0;
    const durationMismatch = totalActivitiesDuration > item.total_duration_mins;

    const canEdit = state.canEdit(item);
    const canDelete = state.canDelete(item);
    const canCopy = state.canCopy(item);
    const copyLabel = state.getCopyLabel();

    return (
      <ListingItem
        key={item.id}
        title={item.name}
        subtitle={item.description}
        icon={<Calendar size={32} />}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Always show view action */}
            <ViewAction size="xs" onClick={() => navigate(paths.view(item.id))} title="View Session" />

            {/* Show edit/delete for templates user can modify */}
            {canEdit && (
              <EditAction size="xs" onClick={() => navigate(paths.edit(item.id))} title="Edit Session" />
            )}
            {canDelete && (
              <DeleteAction size="xs" onClick={() => setShowDeleteModal(item)} title="Delete Session" />
            )}

            {/* Show copy action for system templates (if not already copied) */}
            {isSystemTemplate && canCopy && !alreadyCopied && (
              <CopyAction size="xs" label={copyLabel} onClick={() => handleCopyTemplate(item)} title={copyLabel} />
            )}
          </div>
        }
      >
        <div style={{ display: 'flex', gap: '2.5rem', marginTop: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Session Duration</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
              <Timer size={14} style={{ opacity: 0.5 }} /> {item.total_duration_mins} minutes
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Activities</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
              <ListOrdered size={14} style={{ opacity: 0.5 }} /> {activityCount} {activityCount === 1 ? 'activity' : 'activities'}
            </div>
          </div>

          {activityCount > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>Activity Total</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 600 }}>
                <Timer size={14} style={{ opacity: 0.5 }} /> {totalActivitiesDuration} minutes
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto', alignSelf: 'flex-end' }}>
            {durationMismatch && (
              <Badge variant="warning" size="sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertTriangle size={12} /> DURATION MISMATCH
              </Badge>
            )}
            {isSystemTemplate && (
              <Badge variant="outline" size="sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Lock size={12} /> SYSTEM TEMPLATE
              </Badge>
            )}
            {alreadyCopied && (
              <Badge variant="success" size="sm">COPIED</Badge>
            )}
            {item.is_active ? (
              <Badge variant="success" size="sm">ACTIVE</Badge>
            ) : (
              <Badge variant="warning" size="sm">INACTIVE</Badge>
            )}
          </div>
        </div>
      </ListingItem>
    );
  };

  // Get UI configuration from state
  const uiConfig = state.getSessionManagementUIConfig();
  const pageBackPath = state.getBackPath('sessions', branchId).replace('/sessions', '');

  return (
    <AdminPageLayout
      title="SESSION TEMPLATE MANAGEMENT"
      subtitle={uiConfig.pageSubtitle}
      backPath={pageBackPath}
      maxWidth="1400px"
      primaryActions={
        uiConfig.primaryActionLabel ? <CreateAction onClick={() => navigate(paths.create)} label={uiConfig.primaryActionLabel} /> : null
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
        {/* Custom Sessions - Show for club and branch admins */}
        {uiConfig.showCustomSection && (
          <Section title={uiConfig.customSessionsTitle}>
            <Listing
              loading={loading}
              items={filteredClubSessions}
              filters={filterBar}
              variant="list"
              renderItem={(session) => renderSessionItem(session, false)}
              emptyMessage="No custom session templates created yet. Copy from system templates or create from scratch."
            />
          </Section>
        )}

        {/* System Templates */}
        {uiConfig.showSystemTemplatesSection && (
          <Section title={uiConfig.systemTemplatesTitle}>
            <Listing
              loading={loading}
              items={filteredSystemTemplates}
              filters={filterBar}
              variant="list"
              renderItem={(template) => renderSessionItem(template, true)}
              emptyMessage="No system templates available"
            />
          </Section>
        )}
      </div>

      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={() => handleDeleteSession(showDeleteModal)}
          title="Delete Session Template"
          variant="danger"
          confirmText="Confirm Deletion"
          loading={isDeleting}
        >
          <p>
            Confirm deletion of the <strong>{showDeleteModal.name}</strong> session template.
          </p>
          <p style={{ marginTop: '0.5rem', color: 'var(--color-error)', fontWeight: 600, fontSize: '0.9rem' }}>
            Warning: This session template will be permanently deleted. Existing training sessions created from this template will not be affected.
          </p>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

// Wrap with provider
const SessionTemplateManagement = () => (
  <TemplateStateProvider>
    <SessionTemplateManagementContent />
  </TemplateStateProvider>
);

export default SessionTemplateManagement;
